<!-- components/qr/GenerationInfo.vue -->
<script setup>
import { computed } from "vue";
import {
  InformationCircleIcon,
  ClockIcon,
  CpuChipIcon,
  DocumentTextIcon,
  PhotoIcon,
  SwatchIcon,
} from "@heroicons/vue/24/outline";
import { formatDistanceToNow } from "date-fns";

// Props
const props = defineProps({
  qrData: {
    type: Object,
    required: true,
  },
  generationTime: {
    type: Number,
    default: 0,
  },
  isGenerating: {
    type: Boolean,
    default: false,
  },
});

// Computed
const formattedCreationTime = computed(() => {
  if (!props.qrData?.createdAt) return "Just now";
  return formatDistanceToNow(new Date(props.qrData.createdAt), {
    addSuffix: true,
  });
});

const contentTypeDisplay = computed(() => {
  const types = {
    text: "Plain Text",
    url: "Website URL",
    vcard: "Contact Card",
    wifi: "WiFi Network",
    sms: "SMS Message",
    email: "Email Address",
  };
  return types[props.qrData?.contentType] || "Unknown";
});

const complexityLevel = computed(() => {
  const complexity = props.qrData?.metadata?.complexity || 0;
  if (complexity < 30)
    return { level: "Low", color: "green", description: "Easy to scan" };
  if (complexity < 60)
    return { level: "Medium", color: "yellow", description: "Good balance" };
  if (complexity < 80)
    return { level: "High", color: "orange", description: "More dense" };
  return {
    level: "Very High",
    color: "red",
    description: "May be hard to scan",
  };
});

const fileSize = computed(() => {
  const size = props.qrData?.metadata?.fileSize || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
});

const errorCorrectionLevels = {
  L: { name: "Low", description: "~7% recovery" },
  M: { name: "Medium", description: "~15% recovery" },
  Q: { name: "Quartile", description: "~25% recovery" },
  H: { name: "High", description: "~30% recovery" },
};
</script>

<template>
  <div class="bg-white p-4 tablet:p-6 rounded-lg border border-gray-200 min-w-0">
    <div class="flex items-center space-x-2 mb-6">
      <InformationCircleIcon class="w-5 h-5 text-blue-600" />
      <h3 class="text-lg font-semibold text-gray-800">
        Generation Information
      </h3>
    </div>

    <!-- Loading State -->
    <div v-if="isGenerating" class="space-y-4">
      <div class="animate-pulse">
        <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div class="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div class="h-4 bg-gray-200 rounded w-2/3"></div>
      </div>
      <div class="text-center text-gray-500 text-sm">Generating QR code...</div>
    </div>

    <!-- QR Code Information -->
    <div v-else-if="qrData" class="space-y-4">
      <!-- Basic Info -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div class="space-y-3">
          <div class="flex items-center space-x-2">
            <DocumentTextIcon class="w-4 h-4 text-gray-500" />
            <div>
              <div class="text-sm text-gray-500">Content Type</div>
              <div class="font-medium">{{ contentTypeDisplay }}</div>
            </div>
          </div>

          <div class="flex items-center space-x-2">
            <ClockIcon class="w-4 h-4 text-gray-500" />
            <div>
              <div class="text-sm text-gray-500">Created</div>
              <div class="font-medium">{{ formattedCreationTime }}</div>
            </div>
          </div>

          <div class="flex items-center space-x-2">
            <PhotoIcon class="w-4 h-4 text-gray-500" />
            <div>
              <div class="text-sm text-gray-500">File Size</div>
              <div class="font-medium">{{ fileSize }}</div>
            </div>
          </div>
        </div>

        <div class="space-y-3">
          <div class="flex items-center space-x-2">
            <CpuChipIcon class="w-4 h-4 text-gray-500" />
            <div>
              <div class="text-sm text-gray-500">Complexity</div>
              <div class="flex items-center space-x-2">
                <span
                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                  :class="{
                    'bg-green-100 text-green-800':
                      complexityLevel.color === 'green',
                    'bg-yellow-100 text-yellow-800':
                      complexityLevel.color === 'yellow',
                    'bg-orange-100 text-orange-800':
                      complexityLevel.color === 'orange',
                    'bg-red-100 text-red-800': complexityLevel.color === 'red',
                  }"
                >
                  {{ complexityLevel.level }}
                </span>
                <span class="text-xs text-gray-500">{{
                  complexityLevel.description
                }}</span>
              </div>
            </div>
          </div>

          <div v-if="generationTime" class="flex items-center space-x-2">
            <ClockIcon class="w-4 h-4 text-gray-500" />
            <div>
              <div class="text-sm text-gray-500">Generation Time</div>
              <div class="font-medium">{{ generationTime }}ms</div>
            </div>
          </div>

          <div
            v-if="qrData.metadata?.hasLogo"
            class="flex items-center space-x-2"
          >
            <PhotoIcon class="w-4 h-4 text-gray-500" />
            <div>
              <div class="text-sm text-gray-500">Logo</div>
              <div class="font-medium text-green-600">Included</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Technical Details -->
      <div class="border-t pt-4">
        <h4 class="font-medium text-gray-800 mb-3 flex items-center">
          <CpuChipIcon class="w-4 h-4 mr-2" />
          Technical Details
        </h4>

        <div class="grid grid-cols-2 tablet:grid-cols-4 gap-4 text-sm">
          <div>
            <div class="text-gray-500">Size</div>
            <div class="font-medium">{{ qrData.options?.size || 256 }}px</div>
          </div>

          <div>
            <div class="text-gray-500">Margin</div>
            <div class="font-medium">{{ qrData.options?.margin || 1 }}</div>
          </div>

          <div>
            <div class="text-gray-500">Error Correction</div>
            <div class="font-medium">
              {{
                errorCorrectionLevels[qrData.options?.errorCorrectionLevel]
                  ?.name || "Medium"
              }}
            </div>
          </div>

          <div>
            <div class="text-gray-500">Format</div>
            <div class="font-medium">
              {{ qrData.options?.format?.toUpperCase() || "PNG" }}
            </div>
          </div>
        </div>
      </div>

      <!-- Color Information -->
      <div
        v-if="qrData.options?.darkColor || qrData.options?.lightColor"
        class="border-t pt-4"
      >
        <h4 class="font-medium text-gray-800 mb-3 flex items-center">
          <SwatchIcon class="w-4 h-4 mr-2" />
          Colors
        </h4>

        <div class="flex items-center space-x-6">
          <div
            v-if="qrData.options?.darkColor"
            class="flex items-center space-x-2"
          >
            <div
              class="w-6 h-6 border border-gray-300 rounded"
              :style="{ backgroundColor: qrData.options.darkColor }"
            ></div>
            <div>
              <div class="text-sm text-gray-500">Foreground</div>
              <div class="font-mono text-sm">
                {{ qrData.options.darkColor }}
              </div>
            </div>
          </div>

          <div
            v-if="qrData.options?.lightColor"
            class="flex items-center space-x-2"
          >
            <div
              class="w-6 h-6 border border-gray-300 rounded"
              :style="{ backgroundColor: qrData.options.lightColor }"
            ></div>
            <div>
              <div class="text-sm text-gray-500">Background</div>
              <div class="font-mono text-sm">
                {{ qrData.options.lightColor }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Content Preview -->
      <div class="border-t pt-4">
        <h4 class="font-medium text-gray-800 mb-2 flex items-center">
          <DocumentTextIcon class="w-4 h-4 mr-2" />
          Content Preview
        </h4>
        <div
          class="bg-gray-50 p-3 rounded text-sm font-mono text-gray-700 break-all max-h-20 overflow-y-auto"
        >
          {{
            typeof qrData.content === "string"
              ? qrData.content
              : JSON.stringify(qrData.content, null, 2)
          }}
        </div>
      </div>
    </div>

    <!-- No Data State -->
    <div v-else class="text-center py-8 text-gray-500">
      <InformationCircleIcon class="w-12 h-12 mx-auto mb-3 opacity-50" />
      <p>Generate a QR code to see details</p>
    </div>
  </div>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>